import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import GanttChart, { GanttLegend } from '../../components/shared/GanttChart';
import { printGantt } from '../../utils/printGantt';

const TYPES = [
  { key: '', label: 'All Engagements' },
  { key: 'Implementation', label: 'Implementation' },
  { key: 'POC', label: 'POC' },
];

const STATUS_STYLE = {
  planning:  { bg: '#ede9fe', color: '#7c3aed', label: 'Planning' },
  active:    { bg: '#d1fae5', color: '#065f46', label: 'Active' },
  on_hold:   { bg: '#fef3c7', color: '#b45309', label: 'On Hold' },
  completed: { bg: '#dcfce7', color: '#166534', label: 'Completed' },
  cancelled: { bg: '#f1f5f9', color: '#64748b', label: 'Cancelled' },
};
const STATUS_COLOR = { planning: '#7c3aed', active: '#0B6623', on_hold: '#d97706', completed: '#16a34a', cancelled: '#94a3b8' };

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function KpiCard({ label, value, accent, sub }) {
  return (
    <div className="card" style={{ padding: '18px 20px', flex: 1, minWidth: 150 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .4 }}>{label}</div>
      <div style={{ fontSize: 30, fontWeight: 800, color: accent || 'var(--text-primary)', marginTop: 6, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BarWidget({ rows }) {
  const max = Math.max(...rows.map(r => r.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map(r => {
        const pct = (r.value / max) * 100;
        return (
          <div key={r.label}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{r.label}</span>
            <div style={{ position: 'relative', background: 'var(--n-100)', borderRadius: 6, height: 18 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: r.color, borderRadius: 6, transition: 'width .4s' }} />
              {r.value > 0 && (
                <span style={{ position: 'absolute', left: `calc(${pct}% - 2px)`, top: '50%',
                               transform: 'translate(-100%, -50%)', fontSize: 11, fontWeight: 800, color: '#fff', paddingRight: 4 }}>
                  {r.value}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MilestoneList({ title, items, empty, overdue }) {
  const navigate = useNavigate();
  return (
    <div className="card" style={{ padding: '20px 24px', flex: 1, minWidth: 260 }}>
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16, color: 'var(--text-primary)' }}>{title}</div>
      {items.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{empty}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(m => (
            <div key={m.id} onClick={() => navigate(`/admin/projects/${m.project_id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.title}</div>
                <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.project_title} · {m.project_type}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: overdue ? '#dc2626' : '#0B6623', whiteSpace: 'nowrap' }}>
                {fmtDate(m.end_date)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProjectsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  // Gantt tab
  const [ganttList, setGanttList] = useState([]);
  const [ganttId, setGanttId] = useState('');
  const [ganttProject, setGanttProject] = useState(null);
  const [ganttLoading, setGanttLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (type) params.set('type', type);
      const d = await apiFetch(`/projects/dashboard?${params}`);
      setData(d);
    } catch (_) {}
    setLoading(false);
  }, [type]);

  useEffect(() => { load(); }, [load]);

  // Load the role-scoped project list for the Gantt selector (respects the type filter)
  useEffect(() => {
    if (tab !== 'gantt') return;
    (async () => {
      try {
        const params = new URLSearchParams({ limit: 200 });
        if (type) params.set('type', type);
        const d = await apiFetch(`/projects?${params}`);
        setGanttList(d.projects || []);
        setGanttId(prev => (prev && (d.projects || []).some(p => p.id === prev)) ? prev : (d.projects?.[0]?.id || ''));
      } catch (_) {}
    })();
  }, [tab, type]);

  // Load the selected project's full milestones/tasks
  const loadGanttProject = useCallback(() => {
    if (!ganttId) { setGanttProject(null); return; }
    setGanttLoading(true);
    apiFetch(`/projects/${ganttId}`).then(setGanttProject).catch(() => setGanttProject(null)).finally(() => setGanttLoading(false));
  }, [ganttId]);
  useEffect(() => {
    if (tab !== 'gantt') return;
    loadGanttProject();
  }, [tab, loadGanttProject]);

  const isAgent = user?.role === 'agent';
  const c = data?.counts || {};
  const statusRows = (data?.statusBreakdown || []).map(s => ({
    label: STATUS_STYLE[s.status]?.label || s.status, value: s.count, color: STATUS_COLOR[s.status] || '#64748b',
  }));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">{isAgent ? 'My Projects & POC' : 'Projects & POC Dashboard'}</div>
          <div className="page-sub">{isAgent ? 'Engagements you own or are a member of' : 'Implementation & POC engagements across the company'}</div>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/projects')}>Open Projects List →</button>
      </div>

      {/* Type toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {TYPES.map(t => {
          const active = type === t.key;
          const badge = t.key === 'Implementation' ? data?.typeCounts?.Implementation
                      : t.key === 'POC' ? data?.typeCounts?.POC
                      : (data ? (data.typeCounts.Implementation + data.typeCounts.POC) : null);
          return (
            <button key={t.key} onClick={() => setType(t.key)}
              style={{ padding: '8px 18px', borderRadius: 999, border: '1.5px solid',
                       borderColor: active ? '#0B6623' : 'var(--n-300)',
                       background: active ? '#0B6623' : 'var(--bg-surface)',
                       color: active ? '#fff' : 'var(--text-secondary)',
                       fontWeight: 700, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              {t.label}
              {badge != null && (
                <span style={{ fontSize: 11, fontWeight: 800, padding: '1px 8px', borderRadius: 99,
                               background: active ? 'rgba(255,255,255,.25)' : 'var(--n-100)',
                               color: active ? '#fff' : 'var(--text-secondary)' }}>{badge}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
        {[{ id: 'overview', label: '📊 Overview' }, { id: 'gantt', label: '📈 Gantt Chart' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '10px 22px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
                     color: tab === t.id ? '#0B6623' : 'var(--text-secondary)',
                     borderBottom: `3px solid ${tab === t.id ? '#0B6623' : 'transparent'}`, marginBottom: -2 }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'gantt' ? (
        <div>
          {/* Project selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>Project:</label>
            <select className="input" style={{ maxWidth: 360 }} value={ganttId} onChange={e => setGanttId(e.target.value)}>
              {ganttList.length === 0 && <option value="">No projects</option>}
              {ganttList.map(p => <option key={p.id} value={p.id}>{p.title} · {p.type} · {p.customer_name}</option>)}
            </select>
            <button onClick={loadGanttProject} disabled={!ganttId || ganttLoading} title="Reload the latest data"
              style={{ fontSize: 12.5, fontWeight: 700, color: '#0B6623', background: '#f0fdf4',
                       border: '1.5px solid #bbf7d0', borderRadius: 8, padding: '7px 14px',
                       cursor: (!ganttId || ganttLoading) ? 'not-allowed' : 'pointer', opacity: (!ganttId || ganttLoading) ? 0.5 : 1 }}>
              {ganttLoading ? '…' : '↻ Refresh'}
            </button>
            <div style={{ marginLeft: 'auto' }}><GanttLegend /></div>
            <button className="btn btn-secondary btn-sm" disabled={!ganttProject} onClick={() => ganttProject && printGantt(ganttProject)} style={{ whiteSpace: 'nowrap' }}>🖨 Print (A4)</button>
          </div>
          <div className="card" style={{ padding: 16 }}>
            {ganttLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>Loading…</div>
            ) : !ganttProject ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>Select a project to view its Gantt chart.</div>
            ) : (
              <GanttChart project={ganttProject} />
            )}
          </div>
        </div>
      ) : loading || !data ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* KPI row */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <KpiCard label="Total" value={c.total} accent="#0B6623" />
            <KpiCard label="Active" value={c.active} accent="#065f46" />
            <KpiCard label="Planning" value={c.planning} accent="#7c3aed" />
            <KpiCard label="Completed" value={c.completed} accent="#16a34a" />
            <KpiCard label="Overdue" value={c.overdue} accent={c.overdue > 0 ? '#dc2626' : 'var(--text-primary)'}
              sub={c.overdue > 0 ? 'past target date' : 'on track'} />
          </div>

          {/* Status + POC */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div className="card" style={{ padding: '20px 24px', flex: 1, minWidth: 280 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>By Status</div>
              <BarWidget rows={statusRows} />
            </div>
            <div className="card" style={{ padding: '20px 24px', flex: 1, minWidth: 280 }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>POC Conversion</div>
              {data.pocStats.total === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No POC engagements yet.</div>
              ) : (
                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 90, textAlign: 'center', padding: '14px 8px', background: 'var(--bg-app)', borderRadius: 10 }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0284c7' }}>{data.pocStats.total}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Total POC</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 90, textAlign: 'center', padding: '14px 8px', background: 'var(--bg-app)', borderRadius: 10 }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#0B6623' }}>{data.pocStats.converted}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Converted</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 90, textAlign: 'center', padding: '14px 8px', background: 'var(--bg-app)', borderRadius: 10 }}>
                    <div style={{ fontSize: 26, fontWeight: 800, color: '#b45309' }}>{data.pocStats.prospects}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Prospects</div>
                  </div>
                  <div style={{ width: '100%', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    Conversion rate:&nbsp;
                    <b style={{ color: '#0B6623' }}>
                      {data.pocStats.total ? Math.round((data.pocStats.converted / data.pocStats.total) * 100) : 0}%
                    </b>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Milestones */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <MilestoneList title="⏰ Overdue Milestones" items={data.overdueMs} overdue empty="No overdue milestones — nicely on track." />
            <MilestoneList title="📅 Upcoming Milestones" items={data.upcoming} empty="No upcoming milestones scheduled." />
          </div>

          {/* Recent */}
          <div className="card" style={{ padding: '20px 24px' }}>
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>Recent Engagements</div>
            {data.recent.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No projects yet.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {data.recent.map(p => {
                  const ss = STATUS_STYLE[p.status] || STATUS_STYLE.planning;
                  const pct = p.task_count > 0 ? Math.round((p.tasks_done / p.task_count) * 100) : 0;
                  return (
                    <div key={p.id} onClick={() => navigate(`/admin/projects/${p.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>{p.title}</div>
                        <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.customer_name}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99,
                                     background: p.type === 'POC' ? '#fef9c3' : '#dbeafe', color: p.type === 'POC' ? '#854d0e' : '#1e40af' }}>{p.type}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: ss.bg, color: ss.color }}>{ss.label}</span>
                      {p.overdue && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>overdue</span>}
                      <div style={{ width: 90 }}>
                        <div style={{ height: 6, background: 'var(--n-100)', borderRadius: 99, overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#0B6623' }} />
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8', textAlign: 'right', marginTop: 2 }}>{p.tasks_done}/{p.task_count}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
